/**
 * Strategy Builder Store
 *
 * Zustand store for managing strategy builder state including:
 * - Strategy tree manipulation
 * - Selection and clipboard
 * - Undo/redo history
 * - Import/export
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { parseDSL } from '../services/dslParser';
import type {
  Strategy,
  StrategyNode,
  StrategyMetadata,
  ClipboardState,
  NodeType,
  Condition,
  FilterSignal,
  Selector,
} from '../types/builder';
import {
  createEmptyStrategy,
  createNodeByType,
  createAssetNode,
  cloneNode,
  findNode,
  findParent,
  generateNodeId,
  countNodes,
} from '../types/builder';

const MAX_HISTORY = 50;

// ─── History entry (internal) ────────────────────────────────

interface HistoryEntry {
  strategy: Strategy;
  selectedNodeId: string | null;
  timestamp: number;
  description: string;
}

// ─── Store Interface ─────────────────────────────────────────

// ─── Source tracking ─────────────────────────────────────────

export type BuilderSourceType = 'new' | 'community' | 'user';

interface BuilderSource {
  type: BuilderSourceType;
  strategyId: string | null; // Original strategy ID (for updates)
  strategyName: string | null; // Original name (for display)
}

interface BuilderState {
  // Core state
  strategy: Strategy;
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  clipboard: ClipboardState;
  source: BuilderSource;

  // History
  history: HistoryEntry[];
  historyIndex: number;

  // UI
  isModified: boolean;
  isPropertiesCollapsed: boolean;

  // Strategy actions
  setStrategy: (strategy: Strategy) => void;
  updateMetadata: (updates: Partial<StrategyMetadata>) => void;

  // Selection
  selectNode: (nodeId: string | null) => void;
  setHoveredNode: (nodeId: string | null) => void;

  // Node manipulation
  addNode: (parentId: string, nodeType: NodeType, index?: number) => void;
  updateNode: (nodeId: string, updates: Partial<StrategyNode>) => void;
  deleteNode: (nodeId: string) => void;
  deleteNodeWithChildren: (nodeId: string) => void;
  replaceNode: (nodeId: string, newNode: StrategyNode) => void;
  wrapNode: (nodeId: string, wrapperType: NodeType) => void;
  moveNodeUp: (nodeId: string) => void;
  moveNodeDown: (nodeId: string) => void;

  // Specialized updates
  updateAsset: (nodeId: string, ticker: string, name?: string) => void;
  updateGroupName: (nodeId: string, name: string) => void;
  updateCondition: (nodeId: string, condition: Condition) => void;
  updateWeights: (nodeId: string, weights: number[]) => void;
  updateFilter: (nodeId: string, signal: FilterSignal, selector: Selector) => void;
  updateWindow: (nodeId: string, window: number) => void;

  // Collapse/expand
  toggleCollapse: (nodeId: string) => void;
  collapseAll: () => void;
  expandAll: () => void;

  // Clipboard
  copyNode: (nodeId: string) => void;
  cutNode: (nodeId: string) => void;
  pasteNode: (targetId: string) => void;
  pasteAsSibling: (afterNodeId: string) => void;
  duplicateNode: (nodeId: string) => void;
  clearClipboard: () => void;

  // Deletion
  removeAllChildren: (nodeId: string) => void;

  // History
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // UI
  toggleProperties: () => void;

  // Import/Export
  importStrategy: (strategy: Strategy) => void;
  loadFromDSL: (code: string, sourceType: BuilderSourceType, strategyId: string | null, strategyName: string | null) => void;
  reset: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────

function pushHistory(
  state: Pick<BuilderState, 'history' | 'historyIndex' | 'strategy' | 'selectedNodeId'>,
  description: string,
): Partial<BuilderState> {
  const newEntry: HistoryEntry = {
    strategy: JSON.parse(JSON.stringify(state.strategy)),
    selectedNodeId: state.selectedNodeId,
    timestamp: Date.now(),
    description,
  };
  const trimmedHistory = state.history.slice(0, state.historyIndex + 1);
  const newHistory = [...trimmedHistory, newEntry].slice(-MAX_HISTORY);
  return { history: newHistory, historyIndex: newHistory.length - 1, isModified: true };
}

function updateNodeInTree(
  root: StrategyNode,
  nodeId: string,
  updater: (node: StrategyNode) => StrategyNode,
): StrategyNode {
  if (root.id === nodeId) return updater(root);
  if ('children' in root && Array.isArray(root.children)) {
    return { ...root, children: root.children.map((c) => updateNodeInTree(c, nodeId, updater)) } as StrategyNode;
  }
  if ('child' in root && root.child) {
    return { ...root, child: updateNodeInTree(root.child, nodeId, updater) } as StrategyNode;
  }
  if ('thenBranch' in root) {
    return {
      ...root,
      thenBranch: updateNodeInTree(root.thenBranch, nodeId, updater),
      elseBranch: updateNodeInTree(root.elseBranch, nodeId, updater),
    } as StrategyNode;
  }
  return root;
}

function deleteNodeFromTree(root: StrategyNode, nodeId: string): StrategyNode | null {
  if (root.id === nodeId) return null;
  if ('children' in root && Array.isArray(root.children)) {
    const deleteIndex = root.children.findIndex((c) => c.id === nodeId);
    const newChildren = root.children
      .map((child) => deleteNodeFromTree(child, nodeId))
      .filter((child): child is StrategyNode => child !== null);
    if (newChildren.length === 0) {
      const result = { ...root, children: [createAssetNode('')] } as StrategyNode;
      if (root.type === 'weight-specified') (result as any).weights = [1.0];
      return result;
    }
    if (root.type === 'weight-specified' && deleteIndex >= 0) {
      const newWeights = [...root.weights];
      newWeights.splice(deleteIndex, 1);
      const total = newWeights.reduce((a, b) => a + b, 0);
      const normalizedWeights = total > 0 ? newWeights.map((w) => w / total) : newWeights.map(() => 1 / newWeights.length);
      return { ...root, children: newChildren, weights: normalizedWeights } as StrategyNode;
    }
    return { ...root, children: newChildren } as StrategyNode;
  }
  if ('child' in root && root.child) {
    const newChild = deleteNodeFromTree(root.child, nodeId);
    if (!newChild) return { ...root, child: createAssetNode('') } as StrategyNode;
    return { ...root, child: newChild } as StrategyNode;
  }
  if ('thenBranch' in root) {
    const newThen = deleteNodeFromTree(root.thenBranch, nodeId);
    const newElse = deleteNodeFromTree(root.elseBranch, nodeId);
    return {
      ...root,
      thenBranch: newThen || root.thenBranch,
      elseBranch: newElse || root.elseBranch,
    } as StrategyNode;
  }
  return root;
}

function setCollapseRecursive(node: StrategyNode, collapsed: boolean): StrategyNode {
  const updated = { ...node, collapsed };
  if ('children' in updated && Array.isArray(updated.children))
    (updated as any).children = updated.children.map((c: StrategyNode) => setCollapseRecursive(c, collapsed));
  if ('child' in updated && updated.child)
    (updated as any).child = setCollapseRecursive(updated.child, collapsed);
  if ('thenBranch' in updated) {
    (updated as any).thenBranch = setCollapseRecursive((updated as any).thenBranch, collapsed);
    (updated as any).elseBranch = setCollapseRecursive((updated as any).elseBranch, collapsed);
  }
  return updated;
}

// ─── Store ───────────────────────────────────────────────────

export const useBuilderStore = create<BuilderState>()(
  devtools(
    persist(
      (set, get) => ({
        strategy: createEmptyStrategy(),
        selectedNodeId: null,
        hoveredNodeId: null,
        clipboard: { node: null, operation: null, sourceId: null },
        source: { type: 'new', strategyId: null, strategyName: null },
        history: [],
        historyIndex: -1,
        isModified: false,
        isPropertiesCollapsed: false,

        // ─── Strategy ────────────────────────────────────

        setStrategy: (strategy) => {
          const state = get();
          set({ strategy, selectedNodeId: null, ...pushHistory({ ...state, strategy }, 'Set strategy') });
        },

        updateMetadata: (updates) => {
          const state = get();
          const newStrategy = { ...state.strategy, metadata: { ...state.strategy.metadata, ...updates } };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Update metadata') });
        },

        // ─── Selection ───────────────────────────────────

        selectNode: (nodeId) => set({ selectedNodeId: nodeId }),
        setHoveredNode: (nodeId) => set({ hoveredNodeId: nodeId }),

        // ─── Node CRUD ───────────────────────────────────

        addNode: (parentId, nodeType, index) => {
          const state = get();
          const newNode = createNodeByType(nodeType);
          const parent = findNode(state.strategy.root, parentId);
          if (!parent) return;

          const newRoot = updateNodeInTree(state.strategy.root, parentId, (node) => {
            if ('children' in node && Array.isArray(node.children)) {
              const newChildren = [...node.children];
              const insertIndex = index ?? newChildren.length;
              newChildren.splice(insertIndex, 0, newNode);
              if (node.type === 'weight-specified') {
                const newWeights = [...node.weights];
                newWeights.splice(insertIndex, 0, 1 / newChildren.length);
                const total = newWeights.reduce((a, b) => a + b, 0);
                return { ...node, children: newChildren, weights: newWeights.map((w) => w / total) } as StrategyNode;
              }
              return { ...node, children: newChildren } as StrategyNode;
            }
            if (node.type === 'group' && 'child' in node) {
              if (node.child.type === 'asset' && !node.child.ticker) {
                return { ...node, child: newNode } as StrategyNode;
              }
              return {
                ...node,
                child: { id: generateNodeId(), type: 'weight-equal' as const, children: [node.child, newNode] },
              } as StrategyNode;
            }
            return node;
          });

          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, selectedNodeId: newNode.id, ...pushHistory({ ...state, strategy: newStrategy }, `Add ${nodeType}`) });
        },

        replaceNode: (nodeId, newNode) => {
          const state = get();
          if (state.strategy.root.id === nodeId) {
            const newStrategy = { ...state.strategy, root: newNode };
            set({ strategy: newStrategy, selectedNodeId: newNode.id, ...pushHistory({ ...state, strategy: newStrategy }, 'Replace root') });
            return;
          }
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, () => newNode);
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, selectedNodeId: newNode.id, ...pushHistory({ ...state, strategy: newStrategy }, 'Replace node') });
        },

        wrapNode: (nodeId, wrapperType) => {
          const state = get();
          const nodeToWrap = findNode(state.strategy.root, nodeId);
          if (!nodeToWrap) return;

          const wrapper = createNodeByType(wrapperType);
          const clonedNode = cloneNode(nodeToWrap);
          let wrappedNode: StrategyNode;

          if (wrapper.type === 'group') {
            wrappedNode = { ...wrapper, child: clonedNode } as StrategyNode;
          } else if (wrapper.type === 'if') {
            wrappedNode = { ...wrapper, thenBranch: clonedNode, elseBranch: createNodeByType('asset') } as StrategyNode;
          } else if ('children' in wrapper) {
            wrappedNode = { ...wrapper, children: [clonedNode] } as StrategyNode;
            if (wrapper.type === 'weight-specified') (wrappedNode as any).weights = [1.0];
          } else {
            return;
          }

          if (state.strategy.root.id === nodeId) {
            const newStrategy = { ...state.strategy, root: wrappedNode };
            set({ strategy: newStrategy, selectedNodeId: wrappedNode.id, ...pushHistory({ ...state, strategy: newStrategy }, `Wrap in ${wrapperType}`) });
            return;
          }
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, () => wrappedNode);
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, selectedNodeId: wrappedNode.id, ...pushHistory({ ...state, strategy: newStrategy }, `Wrap in ${wrapperType}`) });
        },

        updateNode: (nodeId, updates) => {
          const state = get();
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, (node) => ({ ...node, ...updates }) as typeof node);
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Update node') });
        },

        deleteNode: (nodeId) => {
          const state = get();
          if (state.strategy.root.id === nodeId) return;
          const newRoot = deleteNodeFromTree(state.strategy.root, nodeId);
          if (!newRoot) return;
          const newStrategy = { ...state.strategy, root: newRoot };
          set({
            strategy: newStrategy,
            selectedNodeId: state.selectedNodeId === nodeId ? null : state.selectedNodeId,
            ...pushHistory({ ...state, strategy: newStrategy }, 'Delete node'),
          });
        },

        deleteNodeWithChildren: (nodeId) => {
          // Same as deleteNode — deleteNodeFromTree already removes descendants
          get().deleteNode(nodeId);
        },

        // ─── Move Up / Down ──────────────────────────────

        moveNodeUp: (nodeId) => {
          const state = get();
          const result = findParent(state.strategy.root, nodeId);
          if (!result || result.key !== 'children' || result.index <= 0) return;
          const parent = result.parent as any;
          const newChildren = [...parent.children];
          [newChildren[result.index - 1], newChildren[result.index]] = [newChildren[result.index], newChildren[result.index - 1]];
          let updates: any = { children: newChildren };
          if (parent.type === 'weight-specified') {
            const newWeights = [...parent.weights];
            [newWeights[result.index - 1], newWeights[result.index]] = [newWeights[result.index], newWeights[result.index - 1]];
            updates.weights = newWeights;
          }
          const newRoot = updateNodeInTree(state.strategy.root, parent.id, (node) => ({ ...node, ...updates }) as StrategyNode);
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Move up') });
        },

        moveNodeDown: (nodeId) => {
          const state = get();
          const result = findParent(state.strategy.root, nodeId);
          if (!result || result.key !== 'children') return;
          const parent = result.parent as any;
          if (result.index >= parent.children.length - 1) return;
          const newChildren = [...parent.children];
          [newChildren[result.index], newChildren[result.index + 1]] = [newChildren[result.index + 1], newChildren[result.index]];
          let updates: any = { children: newChildren };
          if (parent.type === 'weight-specified') {
            const newWeights = [...parent.weights];
            [newWeights[result.index], newWeights[result.index + 1]] = [newWeights[result.index + 1], newWeights[result.index]];
            updates.weights = newWeights;
          }
          const newRoot = updateNodeInTree(state.strategy.root, parent.id, (node) => ({ ...node, ...updates }) as StrategyNode);
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Move down') });
        },

        // ─── Specialized Updates ─────────────────────────

        updateAsset: (nodeId, ticker, name) => {
          const state = get();
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, (node) => {
            if (node.type !== 'asset') return node;
            return { ...node, ticker, name };
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, `Set asset ${ticker}`) });
        },

        updateGroupName: (nodeId, name) => {
          const state = get();
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, (node) => {
            if (node.type !== 'group') return node;
            return { ...node, name };
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Rename group') });
        },

        updateCondition: (nodeId, condition) => {
          const state = get();
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, (node) => {
            if (node.type !== 'if') return node;
            return { ...node, condition };
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Update condition') });
        },

        updateWeights: (nodeId, weights) => {
          const state = get();
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, (node) => {
            if (node.type !== 'weight-specified') return node;
            return { ...node, weights };
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Update weights') });
        },

        updateFilter: (nodeId, signal, selector) => {
          const state = get();
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, (node) => {
            if (node.type !== 'filter') return node;
            return { ...node, signal, selector };
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Update filter') });
        },

        updateWindow: (nodeId, window) => {
          const state = get();
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, (node) => {
            if (node.type !== 'weight-inverse-volatility') return node;
            return { ...node, window };
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Update window') });
        },

        // ─── Collapse/Expand ─────────────────────────────

        toggleCollapse: (nodeId) => {
          set((state) => ({
            strategy: { ...state.strategy, root: updateNodeInTree(state.strategy.root, nodeId, (n) => ({ ...n, collapsed: !n.collapsed })) },
          }));
        },
        collapseAll: () => set((state) => ({ strategy: { ...state.strategy, root: setCollapseRecursive(state.strategy.root, true) } })),
        expandAll: () => set((state) => ({ strategy: { ...state.strategy, root: setCollapseRecursive(state.strategy.root, false) } })),

        // ─── Clipboard ───────────────────────────────────

        copyNode: (nodeId) => {
          const node = findNode(get().strategy.root, nodeId);
          if (!node) return;
          set({ clipboard: { node: cloneNode(node), operation: 'copy', sourceId: nodeId } });
        },

        cutNode: (nodeId) => {
          const state = get();
          const node = findNode(state.strategy.root, nodeId);
          if (!node || state.strategy.root.id === nodeId) return;
          set({ clipboard: { node: cloneNode(node), operation: 'cut', sourceId: nodeId } });
        },

        pasteNode: (targetId) => {
          const state = get();
          const { clipboard } = state;
          if (!clipboard.node) return;
          const target = findNode(state.strategy.root, targetId);
          if (!target) return;
          const nodeToPaste = cloneNode(clipboard.node);
          let newRoot = state.strategy.root;
          if (clipboard.operation === 'cut' && clipboard.sourceId) {
            const deleted = deleteNodeFromTree(newRoot, clipboard.sourceId);
            if (deleted) newRoot = deleted;
          }
          newRoot = updateNodeInTree(newRoot, targetId, (node) => {
            if ('children' in node && Array.isArray(node.children)) {
              const newChildren = [...node.children, nodeToPaste];
              if (node.type === 'weight-specified') {
                const newWeights = [...node.weights, 0];
                const total = newWeights.reduce((a, b) => a + b, 0);
                return { ...node, children: newChildren, weights: total > 0 ? newWeights.map((w) => w / total) : newWeights.map(() => 1 / newWeights.length) } as StrategyNode;
              }
              return { ...node, children: newChildren } as StrategyNode;
            }
            if ('child' in node) {
              return { ...node, child: { id: generateNodeId(), type: 'weight-equal' as const, children: [node.child, nodeToPaste] } } as StrategyNode;
            }
            return node;
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({
            strategy: newStrategy,
            selectedNodeId: nodeToPaste.id,
            clipboard: clipboard.operation === 'cut' ? { node: null, operation: null, sourceId: null } : clipboard,
            ...pushHistory({ ...state, strategy: newStrategy }, 'Paste node'),
          });
        },

        pasteAsSibling: (afterNodeId) => {
          const state = get();
          const { clipboard } = state;
          if (!clipboard.node) return;
          const result = findParent(state.strategy.root, afterNodeId);
          if (!result || result.key !== 'children') return;
          const nodeToPaste = cloneNode(clipboard.node);
          let newRoot = state.strategy.root;
          if (clipboard.operation === 'cut' && clipboard.sourceId) {
            const deleted = deleteNodeFromTree(newRoot, clipboard.sourceId);
            if (deleted) newRoot = deleted;
          }
          const parentId = result.parent.id;
          newRoot = updateNodeInTree(newRoot, parentId, (node) => {
            if ('children' in node && Array.isArray(node.children)) {
              const newChildren = [...node.children];
              // Find afterNodeId's current index (may have shifted after cut)
              const idx = newChildren.findIndex((c) => c.id === afterNodeId);
              const insertAt = idx >= 0 ? idx + 1 : newChildren.length;
              newChildren.splice(insertAt, 0, nodeToPaste);
              if (node.type === 'weight-specified') {
                const newWeights = [...node.weights];
                newWeights.splice(insertAt, 0, 0);
                const total = newWeights.reduce((a, b) => a + b, 0);
                return { ...node, children: newChildren, weights: total > 0 ? newWeights.map((w) => w / total) : newWeights.map(() => 1 / newWeights.length) } as StrategyNode;
              }
              return { ...node, children: newChildren } as StrategyNode;
            }
            return node;
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({
            strategy: newStrategy,
            selectedNodeId: nodeToPaste.id,
            clipboard: clipboard.operation === 'cut' ? { node: null, operation: null, sourceId: null } : clipboard,
            ...pushHistory({ ...state, strategy: newStrategy }, 'Paste as sibling'),
          });
        },

        duplicateNode: (nodeId) => {
          const state = get();
          const node = findNode(state.strategy.root, nodeId);
          if (!node || state.strategy.root.id === nodeId) return;
          const result = findParent(state.strategy.root, nodeId);
          if (!result || result.key !== 'children') return;
          const duplicate = cloneNode(node);
          const parentId = result.parent.id;
          const newRoot = updateNodeInTree(state.strategy.root, parentId, (parent) => {
            if ('children' in parent && Array.isArray(parent.children)) {
              const newChildren = [...parent.children];
              newChildren.splice(result.index + 1, 0, duplicate);
              if (parent.type === 'weight-specified') {
                const newWeights = [...parent.weights];
                newWeights.splice(result.index + 1, 0, newWeights[result.index] || 0);
                const total = newWeights.reduce((a, b) => a + b, 0);
                return { ...parent, children: newChildren, weights: total > 0 ? newWeights.map((w) => w / total) : newWeights.map(() => 1 / newWeights.length) } as StrategyNode;
              }
              return { ...parent, children: newChildren } as StrategyNode;
            }
            return parent;
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, selectedNodeId: duplicate.id, ...pushHistory({ ...state, strategy: newStrategy }, 'Duplicate') });
        },

        clearClipboard: () => set({ clipboard: { node: null, operation: null, sourceId: null } }),

        // ─── Remove All Children ─────────────────────────

        removeAllChildren: (nodeId) => {
          const state = get();
          const newRoot = updateNodeInTree(state.strategy.root, nodeId, (node) => {
            if ('children' in node && Array.isArray(node.children)) {
              const placeholder = createAssetNode('');
              if (node.type === 'weight-specified') {
                return { ...node, children: [placeholder], weights: [1.0] } as StrategyNode;
              }
              return { ...node, children: [placeholder] } as StrategyNode;
            }
            if ('child' in node) {
              return { ...node, child: createAssetNode('') } as StrategyNode;
            }
            return node;
          });
          const newStrategy = { ...state.strategy, root: newRoot };
          set({ strategy: newStrategy, ...pushHistory({ ...state, strategy: newStrategy }, 'Remove all children') });
        },

        // ─── History ─────────────────────────────────────

        undo: () => {
          const state = get();
          if (state.historyIndex <= 0) return;
          const prev = state.history[state.historyIndex - 1];
          set({ strategy: JSON.parse(JSON.stringify(prev.strategy)), selectedNodeId: prev.selectedNodeId, historyIndex: state.historyIndex - 1 });
        },

        redo: () => {
          const state = get();
          if (state.historyIndex >= state.history.length - 1) return;
          const next = state.history[state.historyIndex + 1];
          set({ strategy: JSON.parse(JSON.stringify(next.strategy)), selectedNodeId: next.selectedNodeId, historyIndex: state.historyIndex + 1 });
        },

        canUndo: () => get().historyIndex > 0,
        canRedo: () => get().historyIndex < get().history.length - 1,

        // ─── UI ──────────────────────────────────────────

        toggleProperties: () => set((state) => ({ isPropertiesCollapsed: !state.isPropertiesCollapsed })),

        // ─── Import/Export ───────────────────────────────

        importStrategy: (strategy) => {
          set({
            strategy,
            selectedNodeId: null,
            source: { type: 'new', strategyId: null, strategyName: null },
            history: [{ strategy: JSON.parse(JSON.stringify(strategy)), selectedNodeId: null, timestamp: Date.now(), description: 'Import strategy' }],
            historyIndex: 0,
            isModified: false,
          });
        },

        loadFromDSL: (code, sourceType, strategyId, strategyName) => {
          // Dynamic import not needed — caller should pass parsed strategy via importStrategy
          // This method accepts raw DSL code and parses it inline
          // Import parseDSL at top of file is circular-safe since dslParser only imports types
          const parsed = parseDSL(code);
          set({
            strategy: parsed,
            selectedNodeId: null,
            source: { type: sourceType, strategyId, strategyName },
            history: [{ strategy: JSON.parse(JSON.stringify(parsed)), selectedNodeId: null, timestamp: Date.now(), description: 'Load from DSL' }],
            historyIndex: 0,
            isModified: false,
          });
        },

        reset: () => {
          const newStrategy = createEmptyStrategy();
          set({
            strategy: newStrategy,
            selectedNodeId: null,
            hoveredNodeId: null,
            // NOTE: clipboard is intentionally NOT cleared — allows cross-strategy paste
            source: { type: 'new', strategyId: null, strategyName: null },
            history: [{ strategy: JSON.parse(JSON.stringify(newStrategy)), selectedNodeId: null, timestamp: Date.now(), description: 'New strategy' }],
            historyIndex: 0,
            isModified: false,
          });
        },
      }),
      {
        name: 'tradecraft-builder',
        partialize: (state) => ({
          strategy: state.strategy,
          source: state.source,
          clipboard: state.clipboard,
          isPropertiesCollapsed: state.isPropertiesCollapsed,
        }),
      },
    ),
    { name: 'builder-store' },
  ),
);

// ─── Selector Hooks ──────────────────────────────────────────

export const useStrategy = () => useBuilderStore((s) => s.strategy);
export const useSelectedNodeId = () => useBuilderStore((s) => s.selectedNodeId);
export const useSelectedNode = () => {
  const strategy = useBuilderStore((s) => s.strategy);
  const selectedId = useBuilderStore((s) => s.selectedNodeId);
  if (!selectedId) return null;
  return findNode(strategy.root, selectedId);
};
export const useClipboard = () => useBuilderStore((s) => s.clipboard);
export const useCanUndo = () => useBuilderStore((s) => s.historyIndex > 0);
export const useCanRedo = () => useBuilderStore((s) => s.historyIndex < s.history.length - 1);
export const useNodeCount = () => useBuilderStore((s) => countNodes(s.strategy.root));
